import React, { useMemo, useState } from 'react';

import { groupBy } from 'lodash';
import { Link } from '~/lib/react-router-dom';

import AssessmentIcon from '@mui/icons-material/Assessment';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import CaseClampedText from './CaseClampedText';
import CaseExpandedDetails from './CaseExpandedDetails';
import { OWNER_GROUP_ORDER } from './constants';
import { MOCK_CASES } from './mockCases';
import { useCaseSelection } from './useCaseSelection';
import { useExpandedCaseRows } from './useExpandedCaseRows';

// Widths sum to 100% so the table fits the page; long text clamps instead of scrolling.
// Case Last Modified Date, Booking Check-In Date and Case Reason live only in the expanded row.
const CHECKBOX_COLUMN_WIDTH = '3%';
const EXPAND_COLUMN_WIDTH = '3%';
const COLUMNS = [
  { label: 'Subject', width: '33%' },
  { label: 'Date/Time Opened', width: '12%' },
  { label: 'Open', width: '5%', isCentered: true },
  { label: 'Closed', width: '6%', isCentered: true },
  { label: 'Status', width: '16%' },
  { label: 'Priority', width: '8%' },
  { label: 'Opportunity', width: '14%' },
];

const EMPTY_VALUE = '-';
const SPAN_AFTER_CHECKBOX = COLUMNS.length + 1;
const headerCellSx = { fontWeight: 'bold', verticalAlign: 'bottom' } as const;
const controlCellSx = { verticalAlign: 'middle' } as const;

interface Props {
  buildCaseHref: (caseNumber: string) => string;
}

function stopRowToggle(event: React.MouseEvent) {
  event.stopPropagation();
}

export default function CasesByOwnerPage({ buildCaseHref }: Props) {
  const [search, setSearch] = useState('');
  const { isExpanded, toggle, handleRowClick } = useExpandedCaseRows();
  const { selectedNumbers, isSelected, setSelection } = useCaseSelection();

  const groups = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matching = MOCK_CASES.filter(
      (item) =>
        item.report === 'owner' &&
        (!query ||
          [item.owner, item.subject, item.status, item.reason, item.opportunity]
            .join(' ')
            .toLowerCase()
            .includes(query)),
    );
    const byOwner = groupBy(matching, 'owner');
    const owners = [
      ...OWNER_GROUP_ORDER.filter((owner) => byOwner[owner]),
      ...Object.keys(byOwner).filter((owner) => !OWNER_GROUP_ORDER.includes(owner)),
    ];
    return owners.map((owner) => ({ owner, cases: byOwner[owner] }));
  }, [search]);

  const visibleNumbers = groups.flatMap(({ cases }) => cases.map((item) => item.number));
  const visibleSelectedCount = visibleNumbers.filter(isSelected).length;

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ p: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: 'success.main',
              color: 'common.white',
              display: 'grid',
              placeItems: 'center',
            }}
          >
            <AssessmentIcon />
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Report: Cases
            </Typography>
            <Typography variant="h6" component="h2">
              Campaign Operations Cases
            </Typography>
          </Box>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2}>
          {selectedNumbers.length > 0 && (
            <Typography variant="body2" color="text.secondary">
              {selectedNumbers.length} selected
            </Typography>
          )}
          <TextField
            size="small"
            type="search"
            placeholder="Search..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ width: 240 }}
            slotProps={{
              htmlInput: { 'aria-label': 'Search cases' },
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
        </Stack>
      </Stack>
      <Table size="small" aria-label="Campaign operations cases by owner" sx={{ tableLayout: 'fixed', width: '100%' }}>
        <colgroup>
          <col style={{ width: CHECKBOX_COLUMN_WIDTH }} />
          <col style={{ width: EXPAND_COLUMN_WIDTH }} />
          {COLUMNS.map((column) => (
            <col key={column.label} style={{ width: column.width }} />
          ))}
        </colgroup>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={visibleNumbers.length > 0 && visibleSelectedCount === visibleNumbers.length}
                indeterminate={visibleSelectedCount > 0 && visibleSelectedCount < visibleNumbers.length}
                disabled={visibleNumbers.length === 0}
                onChange={(event) => setSelection(visibleNumbers, event.target.checked)}
                slotProps={{ input: { 'aria-label': 'Select all' } }}
              />
            </TableCell>
            <TableCell />
            {COLUMNS.map((column) => (
              <TableCell key={column.label} align={column.isCentered ? 'center' : 'left'} sx={headerCellSx}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.length === 0 && (
            <TableRow>
              <TableCell colSpan={SPAN_AFTER_CHECKBOX + 1}>
                <Typography color="text.secondary">No cases match that search.</Typography>
              </TableCell>
            </TableRow>
          )}
          {groups.map(({ owner, cases }) => {
            const groupNumbers = cases.map((item) => item.number);
            const groupSelectedCount = groupNumbers.filter(isSelected).length;

            return (
              <React.Fragment key={owner}>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={groupSelectedCount === groupNumbers.length}
                      indeterminate={groupSelectedCount > 0 && groupSelectedCount < groupNumbers.length}
                      onChange={(event) => setSelection(groupNumbers, event.target.checked)}
                      slotProps={{ input: { 'aria-label': `Select ${owner}` } }}
                    />
                  </TableCell>
                  <TableCell colSpan={SPAN_AFTER_CHECKBOX} sx={{ fontWeight: 'bold' }}>
                    <MuiLink component="span" underline="hover">
                      {owner}
                    </MuiLink>{' '}
                    ({cases.length})
                  </TableCell>
                </TableRow>
                {cases.map((item) => {
                  const isOpen = isExpanded(item.number);

                  return (
                    <React.Fragment key={item.number}>
                      <TableRow
                        hover
                        selected={isSelected(item.number)}
                        onClick={(event) => handleRowClick(event, item.number)}
                        sx={{
                          verticalAlign: 'top',
                          cursor: 'pointer',
                          '& > td': isOpen ? { borderBottom: 0 } : undefined,
                        }}
                      >
                        <TableCell padding="checkbox" sx={controlCellSx} onClick={stopRowToggle}>
                          <Checkbox
                            size="small"
                            checked={isSelected(item.number)}
                            onChange={(event) => setSelection([item.number], event.target.checked)}
                            slotProps={{ input: { 'aria-label': `Select case ${item.number}` } }}
                          />
                        </TableCell>
                        <TableCell padding="none" align="center" sx={controlCellSx}>
                          <IconButton
                            size="small"
                            sx={{ p: 0.25 }}
                            aria-label={isOpen ? `Collapse case ${item.number}` : `Expand case ${item.number}`}
                            aria-expanded={isOpen}
                            onClick={() => toggle(item.number)}
                          >
                            {isOpen ? (
                              <KeyboardArrowDownIcon fontSize="small" />
                            ) : (
                              <KeyboardArrowRightIcon fontSize="small" />
                            )}
                          </IconButton>
                        </TableCell>
                        <TableCell>
                          <CaseClampedText text={item.subject} maxLines={3}>
                            <MuiLink component={Link} to={buildCaseHref(item.number)} underline="hover">
                              {item.subject}
                            </MuiLink>
                          </CaseClampedText>
                        </TableCell>
                        <TableCell>
                          <CaseClampedText text={item.opened} />
                        </TableCell>
                        <TableCell align="center" sx={controlCellSx}>
                          <Checkbox
                            size="small"
                            checked={item.isOpen}
                            disabled
                            slotProps={{ input: { 'aria-label': 'Open' } }}
                          />
                        </TableCell>
                        <TableCell align="center" sx={controlCellSx}>
                          <Checkbox
                            size="small"
                            checked={!item.isOpen}
                            disabled
                            slotProps={{ input: { 'aria-label': 'Closed' } }}
                          />
                        </TableCell>
                        <TableCell>
                          <CaseClampedText text={item.status} />
                        </TableCell>
                        <TableCell>
                          <CaseClampedText text={item.priority} maxLines={1} />
                        </TableCell>
                        <TableCell>
                          <CaseClampedText text={item.opportunity || EMPTY_VALUE} />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={SPAN_AFTER_CHECKBOX + 1}
                          sx={{ p: 0, borderBottom: isOpen ? undefined : 0 }}
                        >
                          <Collapse in={isOpen} unmountOnExit>
                            <CaseExpandedDetails caseItem={item} caseHref={buildCaseHref(item.number)} />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
                <TableRow sx={{ bgcolor: 'action.selected' }}>
                  <TableCell />
                  <TableCell colSpan={SPAN_AFTER_CHECKBOX} sx={{ fontWeight: 'bold' }}>
                    Subtotal
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
